import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { emptyDivTestId } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  VMDB_PDB_START_POLL_DELAY,
  VMDB_PDB_START_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationConfirmationProps } from "../OperationTypes";

export interface VmDbStartPdbProps extends OperationConfirmationProps {
  databaseId: string | undefined;
}

const VmDbStartPdb = (
  { location, databaseId, onExecute }: VmDbStartPdbProps,
): JSX.Element => {
  const { invokeCall: invokeStartPDB } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.startVmdbPluggableDatabase },
  );

  React.useEffect(() => {
    const { subscriptionId, resourceGroup, resourceName: pdbName } = parseId(databaseId);
    const resGroup = decodeURIComponent(resourceGroup || "");

    const invokeOptions = {
      onSuccess: onExecute,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.startVMDb(),
          message: Messages.notifications.inProgress.messages.startVMDb(pdbName),
        },
        success: {
          title: Messages.notifications.success.titles.startVMDb(),
          message: Messages.notifications.success.messages.startVMDb(pdbName),
        },
        failure: {
          title: Messages.notifications.failure.titles.start(),
          message: Messages.notifications.failure.messages.startVMDb(pdbName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.VMDB_PDB_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resGroup,
            pluggableDatabaseName: pdbName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.VMDB_STARTED_CHECK_PDB,
          delay: VMDB_PDB_START_POLL_DELAY,
          interval: VMDB_PDB_START_POLL_INTERVAL,
        },
      },
    };

    invokeStartPDB({
      subscriptionId,
      resourceGroupName: resGroup,
      pluggableDatabaseName: pdbName,
    }, invokeOptions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div data-test-id={emptyDivTestId} />;
};

export const newVmDbStartPdb = (
  props: VmDbStartPdbProps,
): JSX.Element => (<VmDbStartPdb {...props} />);
